import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ADVENTURE_REWARD_ICONS } from "../adventure/adventure_box_rewards.js";
import { ImagesHolder } from "../image_loader/images_holder.js";
import { ImgID } from "../image_loader/img_id.js";
import { rewardAmountText } from "../common/reward_format.js";
import type { PrizeShotAssets } from "./prize_shot_assets.js";
import { PRIZE_SHOT_COIN_ART, PRIZE_SHOT_COIN_SOURCE } from "./prize_shot_coin_art.js";
import { PRIZE_SHOT_CELLS, PRIZE_SHOT_COLUMNS, PRIZE_SHOT_ROWS, type PrizeShotReward } from "./prize_shot_rules.js";

type BasicMesh = THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;

interface InstanceBatch
{
  mesh: THREE.InstancedMesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>;
}

interface BalloonBatch extends InstanceBatch
{
  active: number;
}

/** How long and how large a prize shines when it pops. */
interface GlowProfile
{
  size: number;
  fade_in_s: number;
  hold_s: number;
  fade_out_s: number;
  sparkles: boolean;
}

interface Sparkle
{
  mesh: BasicMesh;
  phase: number;
  speed: number;
}

interface Glow
{
  halo: BasicMesh;
  rays: BasicMesh[];
  sparkles: Sparkle[];
  profile: GlowProfile;
  age: number;
}

interface GlowTextures
{
  halo: THREE.Texture;
  rays: THREE.Texture;
  sparkle: THREE.Texture;
}

interface Balloon
{
  cell: number;
  batch: BalloonBatch;
  instance: number;
  material_index: number;
  position: THREE.Vector3;
  center: THREE.Vector3;
  rest_y: number;
  rest_quaternion: THREE.Quaternion;
  quaternion: THREE.Quaternion;
  base_scale: number;
  radius: number;
  collider: RAPIER.Collider | null;
  popped: boolean;
  rendered: boolean;
  /** Seconds since the hit. */
  pop_age: number;
  phase: number;
}

interface PopParticle
{
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  rotation_x: number;
  rotation_z: number;
  scale: number;
  age: number;
}

interface ParticleBatch extends InstanceBatch
{
  particles: PopParticle[];
}

interface Reveal
{
  cell: number;
  mesh: BasicMesh;
  age: number;
  /** A prize, not the cross of an empty balloon: it leaves the shelf once taken. */
  prize: boolean;
  /** Seconds since it was taken; null while it stays. */
  leaving_age: number | null;
  glow: Glow | null;
}

/**
 * The prize shelf on the table: a fixed body with a balloon in every cell and
 * what a popped balloon leaves behind. Balloons are sensors: the aim ray stops
 * on them, the ball flies through.
 */
export class PrizeShotShelf
{
  // nightstand.glb in its own units: open to +Z, standing on y = 0.
  private static readonly HALF_WIDTH = 1.028;
  private static readonly HEIGHT = 2.555;
  private static readonly BACK_OUTER_Z = -0.286;
  private static readonly BACK_INNER_Z = -0.226;
  private static readonly FRONT_Z = 0.114;
  /** Vertical planks, left to right. */
  private static readonly POSTS = [
    [ -1.028, -0.963 ],
    [ -0.537, -0.463 ],
    [ -0.037, 0.037 ],
    [ 0.463, 0.537 ],
    [ 0.963, 1.028 ],
  ];
  /** Horizontal planks, bottom to top. */
  private static readonly PLANKS = [
    [ 0, 0.065 ],
    [ 0.49, 0.565 ],
    [ 0.99, 1.065 ],
    [ 1.49, 1.565 ],
    [ 1.99, 2.065 ],
    [ 2.49, 2.555 ],
  ];
  private static readonly CELL_X = [ -0.75, -0.25, 0.25, 0.75 ];
  /** Floor of each cell, bottom row first. */
  private static readonly CELL_FLOOR_Y = [ 0.065, 0.565, 1.065, 1.565, 2.065 ];
  private static readonly CELL_INNER_W = 0.426;
  private static readonly CELL_INNER_H = 0.425;
  private static readonly CELL_INNER_DEPTH = PrizeShotShelf.FRONT_Z - PrizeShotShelf.BACK_INNER_Z;

  private static readonly BALLOON_COLORS = [ 0xf01822, 0x1d2fe6, 0x46d61c, 0xf7e01c, 0xf41a9c ];
  /** Colour index of each cell, row-major from the top-left. */
  private static readonly CELL_COLORS = [ 0, 4, 1, 3, 1, 0, 2, 4, 2, 3, 0, 1, 1, 4, 0, 2, 2, 4, 0, 3 ];
  /** Balloon gap to the planks at the sides and the back. */
  private static readonly BALLOON_SIDE_GAP = 0.015;
  /** Idle wobble: a twist around the knot axis and small rocks, each at its own speed. */
  private static readonly WOBBLE = [
    { axis: new THREE.Vector3( 0, 1, 0 ), amplitude: 0.5, speed: 1.3 },
    { axis: new THREE.Vector3( 1, 0, 0 ), amplitude: 0.14, speed: 1.05 },
    { axis: new THREE.Vector3( 0, 0, 1 ), amplitude: 0.14, speed: 0.85 },
  ];

  private static readonly REVEAL_S = 0.28;
  /** Side of a prize or cross picture, in model units. */
  private static readonly REVEAL_SIZE = 0.4;
  private static readonly REVEAL_WALL_GAP = 0.02;

  /** Same art as the Mystery Box chest glow, tinted gold. */
  private static readonly GLOW_TINT = "rgb(255, 209, 89)";
  private static readonly GLOW_START_SCALE = 0.6;
  private static readonly GLOW_RAYS = [
    { scale: 2.0, opacity: 0.5, speed: 0.7 },
    { scale: 1.5, opacity: 0.55, speed: -0.3 },
  ];
  private static readonly GLOW_SPARKLES = 7;
  private static readonly POP_PARTICLES = 14;
  private static readonly PRIZE_GLOW: GlowProfile = {
    size: 1,
    fade_in_s: 0.12,
    hold_s: 0.13,
    fade_out_s: 0.25,
    sparkles: false,
  };
  private static readonly GRAND_GLOW: GlowProfile = {
    size: 1.2,
    fade_in_s: 0.35,
    hold_s: 1.3,
    fade_out_s: 0.75,
    sparkles: true,
  };

  private static readonly turned_boxes = new Map<string, THREE.Box3>();
  private static readonly tmp_ab = new THREE.Vector3();
  private static readonly tmp_ap = new THREE.Vector3();
  private static readonly tmp_closest = new THREE.Vector3();
  private static readonly tmp_eye = new THREE.Vector3();
  private static readonly tmp_twist = new THREE.Quaternion();
  private static readonly tmp_matrix = new THREE.Matrix4();
  private static readonly tmp_scale = new THREE.Vector3();
  private static readonly tmp_euler = new THREE.Euler();
  private static readonly tmp_particle_quaternion = new THREE.Quaternion();

  private readonly assets: PrizeShotAssets;
  private readonly root = new THREE.Group();
  private readonly body: RAPIER.RigidBody;
  private readonly scale: number;
  private readonly shelf_material: THREE.MeshStandardMaterial;
  private readonly balloon_materials: THREE.MeshStandardMaterial[];
  /**
   * The same shader three uses for every caster, but the balloons' own: shared
   * with the textured, non-instanced shelf and table it switched program on
   * every caster of every shadow pass, rebuilding the program key each time.
   */
  private readonly balloon_depth_material = new THREE.MeshDepthMaterial();
  // OPT: Bake the shard's authored non-uniform shape into the shared geometry.
  // Every particle can then use an exact uniform instance transform (including correct normals).
  private readonly particle_geometry = new THREE.SphereGeometry( 1, 6, 4 )
    .applyMatrix4( new THREE.Matrix4().makeScale( 1.6, 0.5, 1 ) );
  private readonly reveal_geometry = new THREE.PlaneGeometry( 1, 1 );
  private readonly reveal_textures: THREE.Texture[] = [];
  private readonly reveal_texture_cache = new Map<string, THREE.Texture>();
  private readonly balloon_batches: BalloonBatch[];
  private readonly particle_batches: ParticleBatch[];
  private readonly balloons: Balloon[] = [];
  private readonly reveals: Reveal[] = [];
  private readonly warmup_meshes: BasicMesh[] = [];
  private glow_textures: GlowTextures | null = null;
  private readonly last_reveal_eye = new THREE.Vector3( NaN, NaN, NaN );
  private reveals_need_alignment = true;
  private time = 0;

  /** `claimed`: popped cells whose prize was already taken; the cell stays empty. */
  constructor(
    assets: PrizeShotAssets,
    bottom_center: THREE.Vector3,
    width: number,
    popped: ReadonlyMap<number, PrizeShotReward | null>,
    claimed: ReadonlySet<number> = new Set()
  )
  {
    this.assets = assets;
    this.scale = width / ( PrizeShotShelf.HALF_WIDTH * 2 );
    this.root.position.copy( bottom_center );
    this.root.scale.setScalar( this.scale );

    this.shelf_material = new THREE.MeshStandardMaterial( {
      map: assets.shelf_map,
      normalMap: assets.shelf_normal,
      roughness: 0.55,
      metalness: 0,
    } );
    this.balloon_materials = PrizeShotShelf.BALLOON_COLORS.map( color => new THREE.MeshStandardMaterial( {
      color,
      roughness: 0.22,
      metalness: 0.05,
      emissive: color,
      emissiveIntensity: 0.12,
    } ) );

    // OPT: The 20 balloons use only five materials. Instancing preserves every
    // transform/material/shadow while reducing both colour and shadow passes
    // from 20 draw calls to at most five.
    this.balloon_batches = this.balloon_materials.map( ( material, material_index ) =>
    {
      const capacity = PrizeShotShelf.CELL_COLORS.filter( index => index === material_index ).length;
      const mesh = new THREE.InstancedMesh( assets.balloon_geometry, material, capacity );
      mesh.count = 0;
      mesh.instanceMatrix.setUsage( THREE.DynamicDrawUsage );
      mesh.castShadow = true;
      mesh.customDepthMaterial = this.balloon_depth_material;
      // The shelf is always in view; avoid stale dynamic instance bounds causing culling.
      mesh.frustumCulled = false;
      this.root.add( mesh );
      return { mesh, active: 0 };
    } );

    // OPT: Burst shards used to be 14 independent meshes per balloon (up to
    // hundreds during the final cascade). Five dense instance batches render
    // exactly the same particles with at most five draw calls and no scene churn.
    const particle_capacity = PRIZE_SHOT_CELLS * PrizeShotShelf.POP_PARTICLES;
    this.particle_batches = this.balloon_materials.map( material =>
    {
      const mesh = new THREE.InstancedMesh( this.particle_geometry, material, particle_capacity );
      mesh.count = 0;
      mesh.instanceMatrix.setUsage( THREE.DynamicDrawUsage );
      mesh.frustumCulled = false;
      mesh.visible = false;
      SceneManager.world.add( mesh );
      return { mesh, particles: [] };
    } );

    this.createShelfMesh();
    SceneManager.world.add( this.root );
    this.root.updateMatrixWorld( true );

    this.body = PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation( bottom_center.x, bottom_center.y, bottom_center.z )
    );
    this.createPlankColliders();

    for ( let cell = 0; cell < PRIZE_SHOT_CELLS; cell++ )
    {
      if ( popped.has( cell ) )
        this.showReveal( cell, popped.get( cell ) ?? null, false, claimed.has( cell ) );
      else
        this.createBalloon( cell );
    }
    for ( const batch of this.balloon_batches )
      batch.mesh.visible = batch.active > 0;

    // OPT: Move glow canvas composition out of the first prize-hit frame.
    // If the shared art is ready, the generated pixels are identical.
    this.getGlowTextures();
    this.createWarmupMeshes();
  }

  /**
   * Draws the pictures of prizes that may still come out of this board, so a
   * pop only shows a ready texture. A prize whose icon is not loaded yet is
   * left to be drawn at the pop, as before.
   */
  public prepareRewards( rewards: readonly ( PrizeShotReward | null )[] ): void
  {
    for ( const reward of rewards )
    {
      if ( !reward || this.isRewardIconReady( reward ) )
        this.revealTexture( reward );
    }
  }

  /** Textures a pop may show, for uploading before the round starts. */
  public warmupTextures(): THREE.Texture[]
  {
    const glow = this.getGlowTextures();
    return [ ...this.reveal_textures, ...( glow ? [ glow.halo, glow.rays, glow.sparkle ] : [] ) ];
  }

  public getBodyHandle(): number
  {
    return this.body.handle;
  }

  /** World-space box of the shelf, for framing the camera. */
  public getBox(): THREE.Box3
  {
    const s = this.scale;
    const p = this.root.position;

    return new THREE.Box3(
      new THREE.Vector3( p.x - PrizeShotShelf.HALF_WIDTH * s, p.y, p.z + PrizeShotShelf.BACK_OUTER_Z * s ),
      new THREE.Vector3( p.x + PrizeShotShelf.HALF_WIDTH * s, p.y + PrizeShotShelf.HEIGHT * s, p.z + PrizeShotShelf.FRONT_Z * s ),
    );
  }

  public getFrontZ(): number
  {
    return this.root.position.z + PrizeShotShelf.FRONT_Z * this.scale;
  }

  public cellCenter( cell: number ): THREE.Vector3
  {
    const { col, row_from_bottom } = PrizeShotShelf.cellPos( cell );
    const local = new THREE.Vector3(
      PrizeShotShelf.CELL_X[col],
      PrizeShotShelf.CELL_FLOOR_Y[row_from_bottom] + PrizeShotShelf.CELL_INNER_H / 2,
      0
    );

    return local.multiplyScalar( this.scale ).add( this.root.position );
  }

  /** The balloon the segment a → b passes through, nearest to a first; `pad` is the ball's radius. */
  public hitTest( a: THREE.Vector3, b: THREE.Vector3, pad: number ): number | null
  {
    const ab = PrizeShotShelf.tmp_ab.copy( b ).sub( a );
    const length_sq = ab.lengthSq();
    let best: Balloon | null = null;
    let best_t = Infinity;

    for ( const balloon of this.balloons )
    {
      if ( balloon.popped )
        continue;

      const t = length_sq > 1e-12
        ? THREE.MathUtils.clamp( PrizeShotShelf.tmp_ap.copy( balloon.center ).sub( a ).dot( ab ) / length_sq, 0, 1 )
        : 0;
      const closest = PrizeShotShelf.tmp_closest.copy( a ).addScaledVector( ab, t );
      const reach = balloon.radius + pad;

      if ( closest.distanceToSquared( balloon.center ) <= reach * reach && t < best_t )
      {
        best = balloon;
        best_t = t;
      }
    }

    return best?.cell ?? null;
  }

  public hasBalloon( cell: number ): boolean
  {
    const balloon = this.balloons.find( b => b.cell === cell );
    return !!balloon && !balloon.popped;
  }

  public balloonsLeft(): number
  {
    return this.balloons.filter( balloon => !balloon.popped ).length;
  }

  /** Bursts the balloon and shows what was in it. */
  public pop( cell: number, reward: PrizeShotReward | null ): void
  {
    const balloon = this.balloons.find( b => b.cell === cell );
    if ( !balloon || balloon.popped )
      return;

    balloon.popped = true;
    balloon.pop_age = 0;

    if ( balloon.collider )
    {
      PhysicsWorld.getWorld().removeCollider( balloon.collider, false );
      balloon.collider = null;
    }

    this.createPopParticles( balloon );
    this.showReveal( cell, reward, true );
  }

  /** The prizes in `cells` were taken: they shrink away, the cells stay empty. */
  public removeClaimedPrizes( cells: ReadonlySet<number> ): void
  {
    for ( const reveal of this.reveals )
    {
      if ( reveal.prize && cells.has( reveal.cell ) && reveal.leaving_age === null )
        reveal.leaving_age = 0;
    }
  }

  public update( dt: number ): void
  {
    this.time += dt;

    this.updateBalloons( dt );
    this.updateParticles( dt );
    this.alignRevealsToCamera();
    this.updateGlows( dt );
    this.updateReveals( dt );
  }

  /** Out of sight until disposed, its materials still holding their programs. */
  public hide(): void
  {
    this.root.visible = false;
    for ( const batch of this.particle_batches )
      batch.mesh.visible = false;
  }

  public dispose(): void
  {
    for ( const batch of this.particle_batches )
    {
      SceneManager.world.remove( batch.mesh );
      batch.mesh.dispose();
      batch.particles.length = 0;
    }

    for ( const batch of this.balloon_batches )
      batch.mesh.dispose();

    SceneManager.world.remove( this.root );
    PhysicsWorld.getWorld().removeRigidBody( this.body );

    this.particle_geometry.dispose();
    this.reveal_geometry.dispose();
    this.shelf_material.dispose();
    this.balloon_depth_material.dispose();

    for ( const texture of this.reveal_textures )
      texture.dispose();
    for ( const material of this.balloon_materials )
      material.dispose();
    for ( const mesh of this.warmup_meshes )
      mesh.material.dispose();
    for ( const reveal of this.reveals )
    {
      reveal.mesh.material.dispose();
      this.removeGlow( reveal );
    }

    if ( this.glow_textures )
    {
      this.glow_textures.halo.dispose();
      this.glow_textures.rays.dispose();
      this.glow_textures.sparkle.dispose();
    }
  }

  private createShelfMesh(): void
  {
    const shelf = new THREE.Mesh( this.assets.shelf_geometry, this.shelf_material );
    shelf.castShadow = true;
    shelf.receiveShadow = true;
    this.root.add( shelf );
  }

  private createPlankColliders(): void
  {
    const half_w = PrizeShotShelf.HALF_WIDTH;
    const height = PrizeShotShelf.HEIGHT;
    const back_z = PrizeShotShelf.BACK_INNER_Z;
    const front_z = PrizeShotShelf.FRONT_Z;

    this.addBoxCollider( -half_w, half_w, 0, height, PrizeShotShelf.BACK_OUTER_Z, back_z );

    for ( const [ x0, x1 ] of PrizeShotShelf.POSTS )
      this.addBoxCollider( x0, x1, 0, height, back_z, front_z );

    for ( const [ y0, y1 ] of PrizeShotShelf.PLANKS )
      this.addBoxCollider( -half_w, half_w, y0, y1, back_z, front_z );
  }

  /** A box between the given model-space extents. */
  private addBoxCollider( x0: number, x1: number, y0: number, y1: number, z0: number, z1: number ): void
  {
    const s = this.scale;
    const desc = RAPIER.ColliderDesc
      .cuboid( ( x1 - x0 ) * s / 2, ( y1 - y0 ) * s / 2, ( z1 - z0 ) * s / 2 )
      .setTranslation( ( x0 + x1 ) * s / 2, ( y0 + y1 ) * s / 2, ( z0 + z1 ) * s / 2 )
      .setRestitution( 0.35 );

    PhysicsWorld.getWorld().createCollider( desc, this.body );
  }

  /**
   * Every balloon gets its own pose, as in the design: the knot points left or
   * right, a few lie on their side. It is scaled to fit the cell with room above
   * it, then set down on the cell floor against the back wall.
   */
  private createBalloon( cell: number ): void
  {
    const balloon_radius = 0.209;
    const max_scale = 0.85;
    const headroom = 0.035;
    const front_overhang = 0.04;
    const side_gap = PrizeShotShelf.BALLOON_SIDE_GAP;

    const { col, row_from_bottom } = PrizeShotShelf.cellPos( cell );
    const material_index = PrizeShotShelf.CELL_COLORS[cell] ?? cell % this.balloon_materials.length;
    const batch = this.balloon_batches[material_index];

    const random = ( salt: number ) => PrizeShotShelf.cellRandom( cell, salt );
    const rest_quaternion = PrizeShotShelf.restQuaternion( cell );
    const box = PrizeShotShelf.turnedBoxOf( this.assets.balloon_geometry, cell, rest_quaternion );
    const size = box.getSize( new THREE.Vector3() );
    const base_scale = Math.min(
      max_scale,
      ( PrizeShotShelf.CELL_INNER_H - headroom ) / size.y,
      ( PrizeShotShelf.CELL_INNER_W - 2 * side_gap ) / size.x,
      ( PrizeShotShelf.CELL_INNER_DEPTH - side_gap + front_overhang ) / size.z
    );
    // A little off-centre where the cell has room.
    const slack_x = Math.max( 0, PrizeShotShelf.CELL_INNER_W - 2 * side_gap - size.x * base_scale );
    const box_center_x = PrizeShotShelf.CELL_X[col] + ( random( 5 ) - 0.5 ) * slack_x;
    const rest_y = PrizeShotShelf.CELL_FLOOR_Y[row_from_bottom] - box.min.y * base_scale + 0.004;
    const local_x = box_center_x - ( box.min.x + box.max.x ) / 2 * base_scale;
    const local_z = PrizeShotShelf.BACK_INNER_Z + side_gap - box.min.z * base_scale;
    const position = new THREE.Vector3( local_x, rest_y, local_z );
    const instance = batch.mesh.count++;
    batch.active++;
    this.setInstanceTransform( batch.mesh, instance, position, rest_quaternion, base_scale );
    batch.mesh.instanceMatrix.needsUpdate = true;

    const center = new THREE.Vector3( local_x, rest_y, local_z )
      .multiplyScalar( this.scale )
      .add( this.root.position );
    const radius = balloon_radius * base_scale * this.scale;
    const collider = PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.ball( radius )
        .setSensor( true )
        .setTranslation(
          center.x - this.root.position.x,
          center.y - this.root.position.y,
          center.z - this.root.position.z
        ),
      this.body
    );

    this.balloons.push( {
      cell,
      batch,
      instance,
      material_index,
      position,
      center,
      rest_y,
      rest_quaternion,
      quaternion: rest_quaternion.clone(),
      base_scale,
      radius,
      collider,
      popped: false,
      rendered: true,
      pop_age: 0,
      phase: cell * 1.3,
    } );
  }

  /** The knot points left or right, a few lie on their side: the same pose on every visit. */
  private static restQuaternion( cell: number ): THREE.Quaternion
  {
    const lying_share = 0.12;
    const random = ( salt: number ) => PrizeShotShelf.cellRandom( cell, salt );
    const side = random( 1 ) < 0.5 ? -1 : 1;
    const lying = random( 0 ) < lying_share;
    const tilt = lying ? 1.25 + random( 4 ) * 0.25 : 0.08 + random( 4 ) * 0.42;
    const rotation = new THREE.Euler( ( random( 2 ) - 0.5 ) * 0.4, ( random( 3 ) - 0.5 ) * 1.2, side * tilt );
    return new THREE.Quaternion().setFromEuler( rotation );
  }

  /**
   * Exact extents of the turned mesh at scale 1: its AABB turned with it would
   * overestimate. The pose depends on the cell only, so it is computed once per
   * session (all vertices of 20 balloons were 14 ms of a board at CPU x4).
   */
  private static turnedBoxOf( geometry: THREE.BufferGeometry, cell: number, rest_quaternion: THREE.Quaternion ): THREE.Box3
  {
    const key = `${geometry.uuid}:${cell}`;
    let box = PrizeShotShelf.turned_boxes.get( key );
    if ( !box )
    {
      // Match Object3D.updateMatrix(): Euler updates the quaternion, then the
      // matrix is composed from that quaternion.
      box = PrizeShotShelf.turnedBox( geometry, PrizeShotShelf.tmp_matrix.makeRotationFromQuaternion( rest_quaternion ) );
      PrizeShotShelf.turned_boxes.set( key, box );
    }
    return box;
  }

  /** The balloon extents of every cell ahead of the first shelf (PrizeShotScreen.prefetchAssets). */
  public static prepareBalloonPoses( geometry: THREE.BufferGeometry ): void
  {
    for ( let cell = 0; cell < PRIZE_SHOT_CELLS; cell++ )
      PrizeShotShelf.turnedBoxOf( geometry, cell, PrizeShotShelf.restQuaternion( cell ) );
  }

  private createPopParticles( balloon: Balloon ): void
  {
    const batch = this.particle_batches[balloon.material_index];

    for ( let i = 0; i < PrizeShotShelf.POP_PARTICLES; i++ )
    {
      const size = balloon.radius * ( 0.09 + Math.random() * 0.08 );
      const direction = new THREE.Vector3( Math.random() - 0.5, Math.random() - 0.3, Math.random() * 0.6 ).normalize();
      const speed = balloon.radius * ( 9 + Math.random() * 7 );

      batch.particles.push( {
        position: balloon.center.clone(),
        velocity: direction.multiplyScalar( speed ),
        rotation_x: 0,
        rotation_z: 0,
        scale: size,
        age: 0,
      } );
    }
  }

  private updateBalloons( dt: number ): void
  {
    const swell_s = 0.07;
    const bob_amplitude = 0.008;

    for ( const balloon of this.balloons )
    {
      if ( !balloon.popped )
      {
        this.wobble( balloon, bob_amplitude );
        continue;
      }

      if ( balloon.pop_age >= swell_s )
        continue;

      balloon.pop_age += dt;
      const k = balloon.pop_age / swell_s;
      if ( k >= 1 )
      {
        if ( balloon.rendered )
        {
          this.setInstanceTransform( balloon.batch.mesh, balloon.instance, balloon.position, balloon.quaternion, 0 );
          balloon.rendered = false;
          balloon.batch.active--;
          balloon.batch.mesh.visible = balloon.batch.active > 0;
        }
      }
      else
        this.setInstanceTransform(
          balloon.batch.mesh,
          balloon.instance,
          balloon.position,
          balloon.quaternion,
          balloon.base_scale * ( 1 + 0.3 * k )
        );
    }

    // OPT: Upload each colour's instance buffer once per frame, not once per balloon.
    for ( const batch of this.balloon_batches )
    {
      if ( batch.active > 0 )
        batch.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  private wobble( balloon: Balloon, bob_amplitude: number ): void
  {
    balloon.position.y = balloon.rest_y + Math.sin( this.time * 1.7 + balloon.phase ) * bob_amplitude;
    balloon.quaternion.copy( balloon.rest_quaternion );

    // OPT: A plain loop avoids a callback for every balloon on every frame.
    for ( let i = 0; i < PrizeShotShelf.WOBBLE.length; i++ )
    {
      const { axis, amplitude, speed } = PrizeShotShelf.WOBBLE[i];
      const angle = Math.sin( this.time * speed + balloon.phase * ( 0.7 + i * 0.45 ) ) * amplitude;
      balloon.quaternion.multiply( PrizeShotShelf.tmp_twist.setFromAxisAngle( axis, angle ) );
    }

    this.setInstanceTransform(
      balloon.batch.mesh,
      balloon.instance,
      balloon.position,
      balloon.quaternion,
      balloon.base_scale
    );
  }

  private updateParticles( dt: number ): void
  {
    const life_s = 0.55;

    for ( const batch of this.particle_batches )
    {
      let write = 0;
      for ( const particle of batch.particles )
      {
        particle.age += dt;
        if ( particle.age >= life_s )
          continue;

        particle.velocity.y -= 9 * this.scale * dt;
        particle.velocity.multiplyScalar( 1 - 2.5 * dt );
        particle.position.addScaledVector( particle.velocity, dt );
        particle.rotation_x += dt * 9;
        particle.rotation_z += dt * 7;
        particle.scale *= 1 - dt * 1.4;

        const quaternion = PrizeShotShelf.tmp_particle_quaternion.setFromEuler(
          PrizeShotShelf.tmp_euler.set( particle.rotation_x, 0, particle.rotation_z )
        );
        this.setInstanceTransform( batch.mesh, write, particle.position, quaternion, particle.scale );
        batch.particles[write++] = particle;
      }

      batch.particles.length = write;
      batch.mesh.count = write;
      batch.mesh.visible = write > 0;
      if ( write > 0 )
        batch.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** Writes one exact TRS without allocating temporary render objects. */
  private setInstanceTransform(
    mesh: THREE.InstancedMesh,
    instance: number,
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    scale: number
  ): void
  {
    PrizeShotShelf.tmp_scale.setScalar( scale );
    PrizeShotShelf.tmp_matrix.compose( position, quaternion, PrizeShotShelf.tmp_scale );
    mesh.setMatrixAt( instance, PrizeShotShelf.tmp_matrix );
  }

  private updateReveals( dt: number ): void
  {
    const leave_s = 0.25;
    const reveal_s = PrizeShotShelf.REVEAL_S;

    for ( let i = this.reveals.length - 1; i >= 0; i-- )
    {
      const reveal = this.reveals[i];

      if ( reveal.leaving_age !== null )
      {
        reveal.leaving_age += dt;
        const k = Math.min( 1, reveal.leaving_age / leave_s );
        reveal.mesh.scale.setScalar( ( 1 - k * k ) * PrizeShotShelf.REVEAL_SIZE );

        if ( k >= 1 )
        {
          this.removeGlow( reveal );
          this.root.remove( reveal.mesh );
          reveal.mesh.material.dispose();
          this.reveals.splice( i, 1 );
        }
        continue;
      }

      if ( reveal.age >= reveal_s )
        continue;

      // Pops a little past full size, then settles.
      reveal.age = Math.min( reveal_s, reveal.age + dt );
      const k = reveal.age / reveal_s;
      const pop = k < 0.7
        ? THREE.MathUtils.lerp( 0, 1.15, k / 0.7 )
        : THREE.MathUtils.lerp( 1.15, 1, ( k - 0.7 ) / 0.3 );
      reveal.mesh.scale.setScalar( pop * PrizeShotShelf.REVEAL_SIZE );
    }
  }

  /**
   * A reveal lies on the back wall, but the eye judges it against the front of
   * the cell: each goes where the line from the camera to the centre of its
   * opening meets the back wall.
   */
  private alignRevealsToCamera(): void
  {
    const camera = SceneManager.camera;
    if ( !camera || this.reveals.length === 0 )
      return;

    const eye = this.root.worldToLocal( camera.getWorldPosition( PrizeShotShelf.tmp_eye ) );
    // OPT: The Prize Shot camera is normally fixed. Keep exact camera-facing
    // placement, but skip recalculating every reveal until the eye actually moves.
    if ( !this.reveals_need_alignment && eye.equals( this.last_reveal_eye ) )
      return;
    this.last_reveal_eye.copy( eye );
    this.reveals_need_alignment = false;

    const front_z = PrizeShotShelf.FRONT_Z;
    const wall_z = PrizeShotShelf.BACK_INNER_Z + PrizeShotShelf.REVEAL_WALL_GAP;
    if ( eye.z <= front_z )
      return;

    const t = ( wall_z - eye.z ) / ( front_z - eye.z );
    for ( const reveal of this.reveals )
    {
      const { col, row_from_bottom } = PrizeShotShelf.cellPos( reveal.cell );
      const opening_y = PrizeShotShelf.CELL_FLOOR_Y[row_from_bottom] + PrizeShotShelf.CELL_INNER_H / 2;

      reveal.mesh.position.set(
        eye.x + ( PrizeShotShelf.CELL_X[col] - eye.x ) * t,
        eye.y + ( opening_y - eye.y ) * t,
        wall_z
      );
    }
  }

  /** Equal rewards and crosses are pixel-identical, so they share one texture. */
  private revealTexture( reward: PrizeShotReward | null ): THREE.Texture
  {
    const texture_key = reward
      ? `reward:${reward.type}:${reward.amount}:${reward.grand === true ? 1 : 0}`
      : "cross";
    let texture = this.reveal_texture_cache.get( texture_key );
    if ( !texture )
    {
      texture = new THREE.CanvasTexture( reward ? this.drawReward( reward ) : PrizeShotShelf.drawCross() );
      texture.colorSpace = THREE.SRGBColorSpace;
      this.reveal_texture_cache.set( texture_key, texture );
      this.reveal_textures.push( texture );
    }
    return texture;
  }

  private static createRevealMaterial( texture: THREE.Texture ): THREE.MeshBasicMaterial
  {
    return new THREE.MeshBasicMaterial( {
      map: texture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
    } );
  }

  /**
   * Never shown: a prize picture, a glow layer and the burst shards, kept
   * hidden in the shelf so their shader programs stay alive while it lives.
   * `PrizeShotScreen.warmupFirstUse` draws them once before the round.
   */
  private createWarmupMeshes(): void
  {
    const reveal = new THREE.Mesh( this.reveal_geometry, PrizeShotShelf.createRevealMaterial( this.revealTexture( null ) ) );
    reveal.position.set( PrizeShotShelf.CELL_X[0], PrizeShotShelf.CELL_FLOOR_Y[0], 0 );
    reveal.scale.setScalar( 0 );
    reveal.visible = false;
    this.root.add( reveal );
    this.warmup_meshes.push( reveal );

    const textures = this.getGlowTextures();
    if ( textures )
    {
      const glow = this.createGlowLayer( reveal, textures.halo, 1 );
      this.warmup_meshes.push( glow );
    }
  }

  private isRewardIconReady( reward: PrizeShotReward ): boolean
  {
    return reward.type === "coin" || !!ImagesHolder.getLoadedImage( ADVENTURE_REWARD_ICONS[reward.type] );
  }

  private showReveal( cell: number, reward: PrizeShotReward | null, animate: boolean, claimed = false ): void
  {
    // A prize taken earlier is gone from the shelf; an empty cell keeps its cross.
    if ( claimed && reward )
      return;

    const material = PrizeShotShelf.createRevealMaterial( this.revealTexture( reward ) );
    const mesh = new THREE.Mesh( this.reveal_geometry, material );
    const { col, row_from_bottom } = PrizeShotShelf.cellPos( cell );

    mesh.position.set(
      PrizeShotShelf.CELL_X[col],
      PrizeShotShelf.CELL_FLOOR_Y[row_from_bottom] + PrizeShotShelf.CELL_INNER_H / 2,
      PrizeShotShelf.BACK_INNER_Z + PrizeShotShelf.REVEAL_WALL_GAP
    );
    mesh.scale.setScalar( animate ? 0 : PrizeShotShelf.REVEAL_SIZE );
    mesh.renderOrder = 2;
    this.root.add( mesh );

    // Only at the pop: a prize found on an earlier visit lies there plain.
    const glow = reward && animate
      ? this.createGlow( mesh, reward.grand ? PrizeShotShelf.GRAND_GLOW : PrizeShotShelf.PRIZE_GLOW )
      : null;

    this.reveals.push( {
      cell,
      mesh,
      age: animate ? 0 : PrizeShotShelf.REVEAL_S,
      prize: reward !== null,
      leaving_age: null,
      glow,
    } );
    this.reveals_need_alignment = true;
  }

  /**
   * A soft halo, two ray wheels turning opposite ways and, for the grand prize,
   * sparkles. They ignore depth, so the light spills over the planks around the
   * cell. Nothing shines until the shared glow art is loaded.
   */
  private createGlow( parent: BasicMesh, profile: GlowProfile ): Glow | null
  {
    const textures = this.getGlowTextures();
    if ( !textures )
      return null;

    const halo = this.createGlowLayer( parent, textures.halo, 1 );

    // The wheels start apart, so their rays never line up into one.
    const rays = PrizeShotShelf.GLOW_RAYS.map( ( _, i ) =>
    {
      const mesh = this.createGlowLayer( parent, textures.rays, 1 );
      mesh.rotation.z = i * 0.9;
      return mesh;
    } );

    const sparkles: Sparkle[] = [];
    if ( profile.sparkles )
    {
      for ( let i = 0; i < PrizeShotShelf.GLOW_SPARKLES; i++ )
        sparkles.push( this.createSparkle( parent, textures.sparkle, i ) );
    }

    return { halo, rays, sparkles, profile, age: 0 };
  }

  /** Round the prize, evenly but not on a grid; sparkles go over the icon. */
  private createSparkle( parent: BasicMesh, texture: THREE.Texture, index: number ): Sparkle
  {
    const count = PrizeShotShelf.GLOW_SPARKLES;
    const angle = ( index + 0.3 * Math.sin( index * 2.7 ) ) / count * Math.PI * 2;
    const radius = THREE.MathUtils.lerp( 0.4, 0.6, ( Math.sin( index * 4.1 ) + 1 ) / 2 );

    const mesh = this.createGlowLayer( parent, texture, 3 );
    mesh.position.set( Math.cos( angle ) * radius, Math.sin( angle ) * radius, 0 );

    return {
      mesh,
      phase: index * 2.3,
      speed: THREE.MathUtils.lerp( 2.2, 3.4, ( index % 3 ) / 2 ),
    };
  }

  private createGlowLayer( parent: BasicMesh, map: THREE.Texture, render_order: number ): BasicMesh
  {
    const material = new THREE.MeshBasicMaterial( {
      map,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    } );
    const mesh = new THREE.Mesh( this.reveal_geometry, material );
    mesh.scale.setScalar( 0 );
    mesh.renderOrder = render_order;
    parent.add( mesh );

    return mesh;
  }

  /** Made once, from the shared glow art; null while any of it is not loaded yet. */
  private getGlowTextures(): GlowTextures | null
  {
    if ( this.glow_textures )
      return this.glow_textures;

    const halo = ImagesHolder.getLoadedImage( ImgID.CircleGlow1 ) as CanvasImageSource | null;
    const rays = ImagesHolder.getLoadedImage( ImgID.RaysGlow1 ) as CanvasImageSource | null;
    const sparkle = ImagesHolder.getLoadedImage( ImgID.SparkGlow3 ) as CanvasImageSource | null;
    if ( !halo || !rays || !sparkle )
      return null;

    this.glow_textures = {
      halo: PrizeShotShelf.goldTexture( halo ),
      rays: PrizeShotShelf.goldTexture( rays ),
      sparkle: PrizeShotShelf.goldTexture( sparkle ),
    };
    return this.glow_textures;
  }

  /** Swells in, breathes, and fades out a little larger, so it never just switches off. */
  private updateGlows( dt: number ): void
  {
    const halo_scale = 1.25;
    const halo_opacity = 0.3;
    const sparkle_size = 0.28;
    const out_growth = 0.12;
    const start_scale = PrizeShotShelf.GLOW_START_SCALE;
    const breathe = 1 + 0.05 * Math.sin( this.time * 2.4 );

    for ( const reveal of this.reveals )
    {
      const glow = reveal.glow;
      if ( !glow )
        continue;

      glow.age += dt;
      const profile = glow.profile;
      const fade_out_at = profile.fade_in_s + profile.hold_s;
      if ( glow.age >= fade_out_at + profile.fade_out_s )
      {
        this.removeGlow( reveal );
        continue;
      }

      // Ease out on the way in, ease in on the way out.
      const k_in = Math.min( 1, glow.age / profile.fade_in_s );
      const appear = 1 - Math.pow( 1 - k_in, 3 );
      const k_out = THREE.MathUtils.clamp( ( glow.age - fade_out_at ) / profile.fade_out_s, 0, 1 );
      const vanish = k_out * k_out;
      const alpha = appear * ( 1 - vanish );
      const grow = ( start_scale + ( 1 - start_scale ) * appear + out_growth * vanish ) * profile.size;

      glow.halo.scale.setScalar( halo_scale * grow * breathe );
      glow.halo.material.opacity = halo_opacity * alpha;

      glow.rays.forEach( ( rays, i ) =>
      {
        const wheel = PrizeShotShelf.GLOW_RAYS[i];
        rays.rotation.z += dt * wheel.speed;
        rays.scale.setScalar( wheel.scale * grow );
        rays.material.opacity = wheel.opacity * alpha;
      } );

      for ( const sparkle of glow.sparkles )
      {
        // Each flares up and dies down on its own beat, dark half the time.
        const twinkle = Math.max( 0, Math.sin( this.time * sparkle.speed + sparkle.phase ) );
        sparkle.mesh.scale.setScalar( sparkle_size * ( 0.35 + 0.65 * twinkle ) * grow );
        sparkle.mesh.rotation.z += dt * 0.8;
        sparkle.mesh.material.opacity = alpha * twinkle * twinkle;
      }
    }
  }

  private removeGlow( reveal: Reveal ): void
  {
    const glow = reveal.glow;
    if ( !glow )
      return;

    const meshes = [ glow.halo, ...glow.rays, ...glow.sparkles.map( sparkle => sparkle.mesh ) ];
    for ( const mesh of meshes )
    {
      mesh.removeFromParent();
      mesh.material.dispose();
    }
    reveal.glow = null;
  }

  private drawReward( reward: PrizeShotReward ): HTMLCanvasElement
  {
    const size = 256;
    const text_y = size * 0.82;
    const text_half_h = 32 + 8;

    const canvas = document.createElement( "canvas" );
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext( "2d" )!;

    const is_coin = reward.type === "coin";
    const icon: CanvasImageSource | null = is_coin
      ? this.assets.coins.image as CanvasImageSource
      : ImagesHolder.getLoadedImage( ADVENTURE_REWARD_ICONS[reward.type] ) as CanvasImageSource | null;

    let icon_box: { x: number; y: number; w: number; h: number } | null = null;
    if ( icon )
    {
      const icon_w = is_coin ? PRIZE_SHOT_COIN_ART.width : ( icon as HTMLImageElement ).width || size;
      const icon_h = is_coin ? PRIZE_SHOT_COIN_ART.height : ( icon as HTMLImageElement ).height || size;
      const k = Math.min( size * 0.9 / icon_w, size * 0.72 / icon_h );
      icon_box = {
        x: ( size - icon_w * k ) / 2,
        y: size * 0.06 + ( size * 0.72 - icon_h * k ) / 2,
        w: icon_w * k,
        h: icon_h * k,
      };
    }

    // The icon and the amount under it are centred together.
    const top = Math.min( icon_box?.y ?? text_y - text_half_h, text_y - text_half_h );
    const bottom = text_y + text_half_h;
    ctx.translate( 0, ( size - top - bottom ) / 2 );

    if ( icon && icon_box )
    {
      if ( is_coin )
      {
        ctx.drawImage(
          icon,
          PRIZE_SHOT_COIN_SOURCE.x,
          PRIZE_SHOT_COIN_SOURCE.y,
          PRIZE_SHOT_COIN_SOURCE.width,
          PRIZE_SHOT_COIN_SOURCE.height,
          icon_box.x,
          icon_box.y,
          icon_box.w,
          icon_box.h
        );
      }
      else
      {
        ctx.drawImage( icon, icon_box.x, icon_box.y, icon_box.w, icon_box.h );
      }
    }

    const text = reward.type === "coin" ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount );
    ctx.font = "900 64px Geologica, Nunito, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = 16;
    ctx.strokeStyle = "#003f87";
    ctx.strokeText( text, size / 2, text_y );
    ctx.fillStyle = "#ffffff";
    ctx.fillText( text, size / 2, text_y );

    return canvas;
  }

  /** The brown cross of an empty balloon. */
  private static drawCross(): HTMLCanvasElement
  {
    const size = 256;
    const arm = size * 0.78;
    const thick = size * 0.2;

    const canvas = document.createElement( "canvas" );
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext( "2d" )!;

    ctx.translate( size / 2, size / 2 );
    ctx.rotate( Math.PI / 4 );
    ctx.fillStyle = "#572405";
    ctx.beginPath();
    ctx.roundRect( -arm / 2, -thick / 2, arm, thick, thick * 0.18 );
    ctx.roundRect( -thick / 2, -arm / 2, thick, arm, thick * 0.18 );
    ctx.fill();

    return canvas;
  }

  /** White glow art multiplied by the gold tint, its own alpha kept. */
  private static goldTexture( image: CanvasImageSource ): THREE.CanvasTexture
  {
    const w = ( image as HTMLImageElement ).width || 128;
    const h = ( image as HTMLImageElement ).height || 128;

    const canvas = document.createElement( "canvas" );
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext( "2d" )!;

    ctx.drawImage( image, 0, 0, w, h );
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = PrizeShotShelf.GLOW_TINT;
    ctx.fillRect( 0, 0, w, h );
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage( image, 0, 0, w, h );

    const texture = new THREE.CanvasTexture( canvas );
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  private static cellPos( cell: number ): { col: number; row_from_bottom: number }
  {
    const col = cell % PRIZE_SHOT_COLUMNS;
    const row_from_top = Math.floor( cell / PRIZE_SHOT_COLUMNS );

    return { col, row_from_bottom: PRIZE_SHOT_ROWS - 1 - row_from_top };
  }

  /** A stable pseudo-random number in [0, 1) per cell, so the shelf looks the same on every visit. */
  private static cellRandom( cell: number, salt: number ): number
  {
    let x = Math.imul( cell + 1, 0x9e3779b1 ) ^ Math.imul( salt + 1, 0x85ebca6b );
    x = Math.imul( x ^ ( x >>> 15 ), 0x2c1b3c6d );
    x ^= x >>> 13;

    return ( x >>> 0 ) / 0x100000000;
  }

  private static turnedBox( geometry: THREE.BufferGeometry, matrix: THREE.Matrix4 ): THREE.Box3
  {
    const rotation = new THREE.Matrix4().extractRotation( matrix );
    const position = geometry.getAttribute( "position" );
    const box = new THREE.Box3();
    const point = new THREE.Vector3();

    for ( let i = 0; i < position.count; i++ )
      box.expandByPoint( point.fromBufferAttribute( position, i ).applyMatrix4( rotation ) );

    return box;
  }
}
